import { useField } from "formik";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Result } from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import { ReactComponent as CircleNok } from "@src/modules/shared/components/Results/icons/circle-nok.svg";
import { ReactComponent as CircleOk } from "@src/modules/shared/components/Results/icons/circle-ok.svg";
import ResultCheckboxField from "@src/modules/shared/components/Results/ResultCheckboxField";
import {
  ExclamationBadge,
  LabelCell,
  ToleranceCell,
  ValueCell,
} from "@src/modules/shared/components/Results/ResultRow/ResultRow.components";
import {
  ResultLabelTooltipWrapper,
  ResultsTableRow,
} from "@src/modules/shared/components/Results/ResultsForm.components";
import {
  NotesInput,
  ResultInput,
} from "@src/modules/shared/components/Results/shared/components";
import Tooltip from "@src/modules/shared/components/Tooltip";
import { ReactComponent as Info } from "@src/modules/shared/icons/info.svg";

interface Props {
  result: Result;
  isEdit: boolean;
  resultIndex: number;
  hasRangeColumn: boolean;
  hasRefDiffColumns: boolean;
}

const ResultRow: FunctionComponent<Props> = ({
  result,
  isEdit,
  resultIndex,
  hasRangeColumn,
  hasRefDiffColumns,
}) => {
  const { t } = useTranslation();
  const formattedValue = `${
    result.valueType === "Number" && isNaN(parseFloat(result.value))
      ? `${t("Cannot detect")}`
      : `${result.value}
    ${result.unit ? ` ${result.unit}` : ""}
    `
  }`;
  const [field, meta] = useField(`${resultIndex}.value`);
  const [notesField, notesMeta] = useField(`${resultIndex}.notes`);

  return (
    <ResultsTableRow key={result.id}>
      <LabelCell>
        {!!result.tooltip ? (
          <Tooltip
            description={result.tooltip.text}
            imageSrc={result.tooltip.imagePath}
          >
            <ResultLabelTooltipWrapper>
              {result.label}
              <Info />
            </ResultLabelTooltipWrapper>
          </Tooltip>
        ) : (
          result.label
        )}
      </LabelCell>
      {isEdit && !result.isVirtual ? (
        <ValueCell>
          {result.valueType === "Boolean" ? (
            <ResultCheckboxField {...field} />
          ) : result.valueType === "Notes" ? (
            <NotesInput
              {...notesField}
              value={notesField.value ?? ""}
              hasError={notesMeta.touched && !!notesMeta.error}
              placeholder={result.notes ?? ""}
            />
          ) : (
            <ResultInput
              {...field}
              value={field.value ?? ""}
              hasError={meta.touched && !!meta.error}
              placeholder={formattedValue}
            />
          )}
          {result.isFailed && <ExclamationBadge />}
        </ValueCell>
      ) : (
        <ValueCell>
          {result.valueType === "Boolean" ? (
            result.value === "1" ? (
              <CircleOk />
            ) : (
              <CircleNok />
            )
          ) : result.valueType === "Notes" ? (
            result.notes
          ) : (
            formattedValue
          )}
          {result.isFailed && <ExclamationBadge />}
        </ValueCell>
      )}
      <ToleranceCell>{result.tolerance}</ToleranceCell>
      {hasRangeColumn ? (
        <ToleranceCell>{result.range}</ToleranceCell>
      ) : (
        hasRefDiffColumns && (
          <>
            <ToleranceCell>{result.referenceValue}</ToleranceCell>
            <ToleranceCell>{result.difference}</ToleranceCell>
          </>
        )
      )}
    </ResultsTableRow>
  );
};

export default ResultRow;
